import { z } from 'zod'

export const calendarHeaderSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().max(1000).optional().or(z.literal('')),
})

export type CalendarHeaderFormValues = z.infer<typeof calendarHeaderSchema>

export const calendarEventSchema = z.object({
  name: z.string().min(1, 'Event name is required').max(255),
  description: z.string().max(1000).optional().or(z.literal('')),
})

export type CalendarEventFormValues = z.infer<typeof calendarEventSchema>

export const milestoneSchema = z
  .object({
    name: z.string().min(1, 'Milestone name is required').max(255),
    description: z.string().max(1000).optional().or(z.literal('')),
    milestone_type: z.enum(['FREEFORM', 'STANDARDIZED']),
    date_mode: z.enum(['SPECIFIC', 'CALCULATED']),
    // Specific date fields
    specific_date: z.string().optional().or(z.literal('')),
    fiscal_year: z.coerce
      .number()
      .int()
      .min(2000)
      .max(2100)
      .optional()
      .or(z.literal(0)),
    fiscal_week: z.coerce
      .number()
      .int()
      .min(0)
      .max(53)
      .optional()
      .or(z.literal(0)),
    // Calculated date fields
    calc_direction: z.enum(['ADD', 'SUBTRACT']).optional(),
    calc_quantity: z.coerce.number().int().positive().optional(),
    calc_unit: z.enum(['DAY', 'WEEK', 'MONTH']).optional(),
    calc_from_milestone_id: z.string().optional().or(z.literal('')),
    // Standardized rule fields
    rule_code: z.string().optional().or(z.literal('')),
    rule_configuration: z.record(z.unknown()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.date_mode === 'SPECIFIC') {
      const hasSpecificDate = data.specific_date && data.specific_date !== ''
      const hasFiscal =
        data.fiscal_year &&
        data.fiscal_year > 0 &&
        data.fiscal_week &&
        data.fiscal_week > 0
      if (!hasSpecificDate && !hasFiscal) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either a specific date or fiscal year + week is required',
          path: ['specific_date'],
        })
      }
    }

    if (data.date_mode === 'CALCULATED') {
      if (!data.calc_direction) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Direction is required for calculated dates',
          path: ['calc_direction'],
        })
      }
      if (!data.calc_quantity || data.calc_quantity <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Quantity must be a positive number',
          path: ['calc_quantity'],
        })
      }
      if (!data.calc_unit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Unit is required for calculated dates',
          path: ['calc_unit'],
        })
      }
      if (!data.calc_from_milestone_id || data.calc_from_milestone_id === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Reference milestone is required for calculated dates',
          path: ['calc_from_milestone_id'],
        })
      }
    }

    if (data.milestone_type === 'STANDARDIZED') {
      if (!data.rule_code || data.rule_code === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Rule is required for standardized milestones',
          path: ['rule_code'],
        })
      }
    }
  })

export type MilestoneFormValues = z.infer<typeof milestoneSchema>
